import React from 'react';
import { ModuleLink } from '../../../components/ModuleLink';
import { AnchoredMenuPanel, useAnchoredMenu } from '../../../components/AnchoredMenu';
import { ArrowLeftRight, Download, Mail, MoreHorizontal, Printer, Undo2, FileMinus } from 'lucide-react';
import { printPdfFromUrl } from '../../../utils/printPdf';

const PANEL_WIDTH = 272;

type InvoiceOutgoingRowMenuProps = {
  /** Datei des Belegs (`/api/invoices/:id/file`); `null` = keine Datei (z. B. Sage-Import ohne PDF). */
  file: { url: string; filename: string; isPdf: boolean } | null;
  /** Öffnet das E-Mail-Fenster mit dem Beleg als Anhang; nur mit Datei. */
  onSendMail?: (() => void) | null;
  /** Eingangsrechnungen und Angebote: kein Storno/Gutschrift, nur weitere Aktionen. */
  showStornoAndCredit?: boolean;
  /** Umwandlung Eingang <-> Ausgang; nur übergeben, wenn der Beleg umwandelbar ist. */
  reclassify?: { label: string; onSelect: () => void } | null;
  cancelled: boolean;
  hasLead: boolean;
  hasLedgerSnapshot: boolean;
  stornoHref: string | null;
  creditHref: string | null;
};

export function InvoiceOutgoingRowMenu({
  file,
  onSendMail = null,
  showStornoAndCredit = true,
  reclassify = null,
  cancelled,
  hasLead,
  hasLedgerSnapshot,
  stornoHref,
  creditHref,
}: InvoiceOutgoingRowMenuProps) {
  const fileItems = file ? 1 + (file.isPdf ? 1 : 0) + (onSendMail ? 1 : 0) : 0;
  const itemCount = fileItems + (showStornoAndCredit ? 2 : 0) + (reclassify ? 1 : 0);
  const menu = useAnchoredMenu({ panelWidth: PANEL_WIDTH, panelHeight: itemCount * 40 + 16 });
  const { open, setOpen, triggerRef } = menu;

  // Auch stornierte Belege lassen sich drucken und herunterladen; Storno/Gutschrift sind dann einzeln gesperrt.
  const triggerDisabled = !file && !showStornoAndCredit && !reclassify;
  const hasDocumentActions = showStornoAndCredit || Boolean(reclassify);
  const stornoDisabled = cancelled || !hasLead || !hasLedgerSnapshot || !stornoHref;
  const creditDisabled = cancelled || !hasLead || !hasLedgerSnapshot || !creditHref;
  const stornoTitle = cancelled
    ? 'Diese Rechnung ist bereits storniert.'
    : !hasLead
      ? 'Kein Auftrag verknüpft. Weisen Sie der Rechnung einen Lead zu, oder legen Sie die Stornorechnung manuell im passenden Lead an („Neues Dokument“ → Stornorechnung).'
      : !hasLedgerSnapshot
        ? 'Für diese Rechnung liegt kein Positions-Snapshot vor. Legen Sie die Stornorechnung manuell im Lead an: „Neues Dokument“ → Stornorechnung.'
        : 'Rechnung stornieren';
  const creditTitle = cancelled
    ? 'Diese Rechnung ist bereits storniert.'
    : !hasLead
      ? 'Kein Auftrag verknüpft. Weisen Sie der Rechnung einen Lead zu, oder legen Sie die Gutschrift manuell im passenden Lead an („Neues Dokument“ → Gutschrift).'
      : !hasLedgerSnapshot
        ? 'Für diese Rechnung liegt kein Positions-Snapshot vor. Legen Sie die Gutschrift manuell im Lead an: „Neues Dokument“ → Gutschrift.'
        : 'Gutschrift erstellen';

  const itemClass = (disabled: boolean) =>
    `flex w-full items-center gap-2 whitespace-nowrap rounded-md px-3 py-2 text-left text-sm ${
      disabled ? 'cursor-not-allowed text-slate-400' : 'text-app-text-muted hover:bg-app-hover'
    }`;

  return (
    <>
      <button
        ref={triggerRef}
        type="button"
        disabled={triggerDisabled}
        onClick={(e) => {
          e.stopPropagation();
          if (triggerDisabled) return;
          setOpen((v) => !v);
        }}
        className="h-7 w-7 inline-flex items-center justify-center rounded-lg text-app-text-subtle hover:bg-app-hover hover:text-app-text disabled:cursor-not-allowed disabled:text-slate-300"
        aria-label="Aktionen"
        aria-haspopup="menu"
        aria-expanded={open}
        title="Aktionen"
      >
        <MoreHorizontal className="h-4 w-4" />
      </button>
      <AnchoredMenuPanel menu={menu} width={PANEL_WIDTH}>
        {file?.isPdf ? (
          <button
            type="button"
            role="menuitem"
            onClick={() => {
              setOpen(false);
              // Klappt der Druck nicht (z. B. Popup/iframe blockiert), PDF im neuen Tab öffnen — dort lässt es sich drucken.
              printPdfFromUrl(file.url).catch(() => window.open(file.url, '_blank', 'noopener'));
            }}
            className={itemClass(false)}
          >
            <Printer className="h-4 w-4 text-app-text-subtle" />
            Drucken
          </button>
        ) : null}
        {file ? (
          <a
            role="menuitem"
            href={`${file.url}?download=1`}
            download={file.filename || true}
            onClick={() => setOpen(false)}
            className={itemClass(false)}
          >
            <Download className="h-4 w-4 text-app-text-subtle" />
            Herunterladen
          </a>
        ) : null}
        {file && onSendMail ? (
          <button
            type="button"
            role="menuitem"
            onClick={() => {
              setOpen(false);
              onSendMail();
            }}
            className={itemClass(false)}
          >
            <Mail className="h-4 w-4 text-app-text-subtle" />
            Per E-Mail senden
          </button>
        ) : null}
        {file && hasDocumentActions ? <div className="my-1 border-t border-app-border" role="separator" /> : null}
        {!showStornoAndCredit ? null : stornoDisabled || !stornoHref ? (
          <span role="menuitem" aria-disabled="true" title={stornoTitle} className={itemClass(true)}>
            <Undo2 className="h-4 w-4" />
            Rechnung stornieren
          </span>
        ) : (
          <ModuleLink
            role="menuitem"
            to={stornoHref}
            title={stornoTitle}
            onClick={() => setOpen(false)}
            className={itemClass(false)}
            fallbackClassName={itemClass(true)}
          >
            <Undo2 className="h-4 w-4 text-app-text-subtle" />
            Rechnung stornieren
          </ModuleLink>
        )}
        {!showStornoAndCredit ? null : creditDisabled || !creditHref ? (
          <span role="menuitem" aria-disabled="true" title={creditTitle} className={itemClass(true)}>
            <FileMinus className="h-4 w-4" />
            Gutschrift erstellen
          </span>
        ) : (
          <ModuleLink
            role="menuitem"
            to={creditHref}
            title={creditTitle}
            onClick={() => setOpen(false)}
            className={itemClass(false)}
            fallbackClassName={itemClass(true)}
          >
            <FileMinus className="h-4 w-4 text-app-text-subtle" />
            Gutschrift erstellen
          </ModuleLink>
        )}
        {reclassify ? (
          <button
            type="button"
            role="menuitem"
            onClick={() => {
              setOpen(false);
              reclassify.onSelect();
            }}
            className={itemClass(false)}
          >
            <ArrowLeftRight className="h-4 w-4 text-app-text-subtle" />
            {reclassify.label}
          </button>
        ) : null}
      </AnchoredMenuPanel>
    </>
  );
}
